import { TinyColor } from '@ctrl/tinycolor';

export interface HsvColor {
  h: number;
  s: number;
  v: number;
  a: number;
}

export const HEX_DRAFT_REGEX = /^#?([0-9A-Fa-f]{0,8})$/;

const HEX_COMPLETE_REGEX =
  /^#?(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;

export const NEUTRAL_HSV: HsvColor = { h: 0, s: 0, v: 1, a: 1 };

export const CSS_NAMED_COLORS = [
  'AliceBlue',
  'AntiqueWhite',
  'Aqua',
  'Aquamarine',
  'Azure',
  'Beige',
  'Bisque',
  'Black',
  'BlanchedAlmond',
  'Blue',
  'BlueViolet',
  'Brown',
  'BurlyWood',
  'CadetBlue',
  'Chartreuse',
  'Chocolate',
  'Coral',
  'CornflowerBlue',
  'Cornsilk',
  'Crimson',
  'Cyan',
  'DarkBlue',
  'DarkCyan',
  'DarkGoldenRod',
  'DarkGray',
  'DarkGreen',
  'DarkKhaki',
  'DarkMagenta',
  'DarkOliveGreen',
  'DarkOrange',
  'DarkOrchid',
  'DarkRed',
  'DarkSalmon',
  'DarkSeaGreen',
  'DarkSlateBlue',
  'DarkSlateGray',
  'DarkTurquoise',
  'DarkViolet',
  'DeepPink',
  'DeepSkyBlue',
  'DimGray',
  'DodgerBlue',
  'FireBrick',
  'FloralWhite',
  'ForestGreen',
  'Fuchsia',
  'Gainsboro',
  'GhostWhite',
  'Gold',
  'GoldenRod',
  'Gray',
  'Green',
  'GreenYellow',
  'HoneyDew',
  'HotPink',
  'IndianRed',
  'Indigo',
  'Ivory',
  'Khaki',
  'Lavender',
  'LavenderBlush',
  'LawnGreen',
  'LemonChiffon',
  'LightBlue',
  'LightCoral',
  'LightCyan',
  'LightGoldenRodYellow',
  'LightGray',
  'LightGreen',
  'LightPink',
  'LightSalmon',
  'LightSeaGreen',
  'LightSkyBlue',
  'LightSlateGray',
  'LightSteelBlue',
  'LightYellow',
  'Lime',
  'LimeGreen',
  'Linen',
  'Magenta',
  'Maroon',
  'MediumAquaMarine',
  'MediumBlue',
  'MediumOrchid',
  'MediumPurple',
  'MediumSeaGreen',
  'MediumSlateBlue',
  'MediumSpringGreen',
  'MediumTurquoise',
  'MediumVioletRed',
  'MidnightBlue',
  'MintCream',
  'MistyRose',
  'Moccasin',
  'NavajoWhite',
  'Navy',
  'OldLace',
  'Olive',
  'OliveDrab',
  'Orange',
  'OrangeRed',
  'Orchid',
  'PaleGoldenRod',
  'PaleGreen',
  'PaleTurquoise',
  'PaleVioletRed',
  'PapayaWhip',
  'PeachPuff',
  'Peru',
  'Pink',
  'Plum',
  'PowderBlue',
  'Purple',
  'RebeccaPurple',
  'Red',
  'RosyBrown',
  'RoyalBlue',
  'SaddleBrown',
  'Salmon',
  'SandyBrown',
  'SeaGreen',
  'SeaShell',
  'Sienna',
  'Silver',
  'SkyBlue',
  'SlateBlue',
  'SlateGray',
  'Snow',
  'SpringGreen',
  'SteelBlue',
  'Tan',
  'Teal',
  'Thistle',
  'Tomato',
  'Turquoise',
  'Violet',
  'Wheat',
  'White',
  'WhiteSmoke',
  'Yellow',
  'YellowGreen',
];

export const toHexOrEmpty = (value: string): string => {
  if (!value) return '';
  const parsed = new TinyColor(value);
  return parsed.isValid ? parsed.toHexString() : '';
};

export const normalizeHex = (value: string): string | undefined => {
  const digits = value.trim().replace(/^#/, '');
  if (!HEX_COMPLETE_REGEX.test(digits)) return undefined;
  const parsed = new TinyColor(`#${digits}`);
  return parsed.isValid ? parsed.toHexString() : undefined;
};

export const toHsv = (value: string): HsvColor => {
  const parsed = new TinyColor(value);
  if (!parsed.isValid) return NEUTRAL_HSV;
  const hsv = parsed.toHsv();
  return {
    h: hsv.h ?? 0,
    s: hsv.s ?? 0,
    v: hsv.v ?? 1,
    a: hsv.a ?? 1,
  };
};

export const hsvToHex = (color: HsvColor): string =>
  new TinyColor(color).toHexString();

export const hsvToRgb = (color: HsvColor): string =>
  new TinyColor(color).toRgbString();

export const namedColorForHex = (value: string): string | undefined => {
  const parsed = new TinyColor(value);
  if (!parsed.isValid) return undefined;
  const target = parsed.toHexString().toLowerCase();
  return CSS_NAMED_COLORS.find(
    (name) => new TinyColor(name).toHexString().toLowerCase() === target
  );
};
